/**
 * RaceTrace Live: the field on the track, shared by every tab (useLiveField.ts). What the track map
 * knew alone before: where each car is (its motion from the timing's positions), who is in the pit
 * lane, who has stopped, and when the flag changes. These are the pure parts; the hook polls the
 * positions and holds the state.
 */
import type { RaceChartData } from "@shared/types";
import { carKey, carNo } from "./car-number";
import {
  EST,
  IN_PIT,
  NO_GPS,
  OVERDUE,
  isStopped,
  type CarLaps,
  type CarMotion,
} from "./track-motion";
import type { PitVisit } from "./RunningOrder";
import type { TrackPlace } from "./LiveTrackOrder";

/** The circuit's outline from the server (metres; points ordered from the start/finish line). */
export interface TrackOutline {
  len: number;
  w: number;
  h: number;
  pts: [number, number][];
  ds: number[];
}

/** Flags that bring out the safety car (a full-course caution). */
const CAUTION_FLAGS = new Set(["Yellow", "Code 35", "Code 60", "FCY"]);
export function isCaution(flag: string | null | undefined): boolean {
  return !!flag && CAUTION_FLAGS.has(flag);
}

/** The cars in the pit lane after a reading: one already there keeps its visit; one new to it went in after its laps so far. */
export function pitVisitsNow(
  prev: Map<number, PitVisit>,
  inPit: string[],
  data: RaceChartData
): Map<number, PitVisit> {
  const out = new Map<number, PitVisit>();
  for (const n of inPit) {
    const key = carKey(data, n);
    if (key === null || !data.cars[String(key)]) continue;
    const laps = data.cars[String(key)].laps;
    out.set(
      key,
      prev.get(key) ?? { afterLap: laps.length ? laps[laps.length - 1].l : 0 }
    );
  }
  return out;
}

/** A typical pace (m/s) for a car until its own is known: the lap over the field's median green lap. */
export function typicalSpeedOf(data: RaceChartData, track: TrackOutline | null): number {
  if (!track) return 40;
  const xs = Object.values(data.cars)
    .map((c) => c.laps[c.laps.length - 1]?.ltSec)
    .filter(
      (v): v is number =>
        typeof v === "number" &&
        v > 30 &&
        (data.greenPaceCutoff <= 0 || v < data.greenPaceCutoff)
    )
    .sort((a, b) => a - b);
  return xs.length ? track.len / xs[Math.floor(xs.length / 2)] : 40;
}

/**
 * Each car's own pace (m/s) from its laps, by published number: the median of its last five laps
 * out of the pits, within 60-140% of the typical pace; and its completed laps.
 */
export function lapPaceOf(
  data: RaceChartData,
  track: TrackOutline | null,
  typicalSpeed: number
): Map<string, CarLaps> {
  const out = new Map<string, CarLaps>();
  if (!track) return out;
  for (const [k, c] of Object.entries(data.cars)) {
    const xs = c.laps
      .filter((l) => l.ltSec > 30 && !l.pit)
      .slice(-5)
      .map((l) => l.ltSec)
      .sort((a, b) => a - b);
    const pace = xs.length
      ? Math.max(
          0.6 * typicalSpeed,
          Math.min(1.4 * typicalSpeed, track.len / xs[Math.floor(xs.length / 2)])
        )
      : null;
    const last = c.laps[c.laps.length - 1];
    out.set(carNo(data, Number(k)), {
      pace,
      laps: last ? last.l : 0,
      lastLapS: last && last.ltSec > 0 ? last.ltSec : null,
    });
  }
  return out;
}

/** Every running car's place on the track now, and every car in the pit lane (also one the map no longer moves). */
export function carPlaces(
  data: RaceChartData,
  motions: Map<string, CarMotion>,
  out: Set<number>,
  pitVisits: Map<number, PitVisit>
): TrackPlace[] {
  const places: TrackPlace[] = [...motions.values()].flatMap((m) => {
    const k = carKey(data, m.num);
    // Overdue with no position: where it is is not known, so it has no place.
    if (k === null || !data.cars[String(k)] || out.has(k) || m.flags & OVERDUE) return [];
    return [{ key: k, d: m.shown, inPit: (m.flags & IN_PIT) !== 0 || pitVisits.has(k) }];
  });
  for (const k of pitVisits.keys())
    if (!out.has(k) && !places.some((p) => p.key === k))
      places.push({ key: k, d: 0, inPit: true });
  return places;
}

/**
 * The cars stopped now (chart keys), one rule for every tab: by GPS while the car has a fix
 * (track-motion.ts isStopped: its fix frozen and its next lap overdue, or sooner at 0 mph, or
 * with a good signal); without one, also when it is placed by its lap time (EST), by its laps (live-strategy.ts stoppedByLaps: no lap for 3 of its green paces). Not a car
 * that is out (not running) or in the pit lane.
 */
export function stoppedCars({
  data,
  motions,
  unplaced,
  out,
  pitVisits,
  lapStopped,
  now,
  len,
}: {
  data: RaceChartData;
  motions: Map<string, CarMotion>;
  /** Cars the last reading gave no place (published numbers). */
  unplaced: string[];
  out: Set<number>;
  pitVisits: Map<number, PitVisit>;
  lapStopped: Set<number>;
  now: number;
  len: number | null;
}): Set<number> {
  const byKey = new Map<number, CarMotion>();
  for (const m of motions.values()) {
    const k = carKey(data, m.num);
    if (k !== null) byKey.set(k, m);
  }
  const noFix = new Set(
    unplaced.map((n) => carKey(data, n)).filter((k): k is number => k !== null)
  );
  const stopped = new Set<number>();
  for (const k of Object.keys(data.cars).map(Number)) {
    if (out.has(k) || pitVisits.has(k)) continue;
    const m = byKey.get(k);
    const gps =
      m !== undefined &&
      len !== null &&
      !noFix.has(k) &&
      !(m.flags & (NO_GPS | EST | IN_PIT));
    if (gps ? isStopped(m, now, len) : lapStopped.has(k)) stopped.add(k);
  }
  return stopped;
}

/** What a flag change means for the cars: a caution out, green again, a red flag, the finish. */
export type FlagEvent = "caution" | "green" | "red" | "checkered";

export interface FlagChange {
  event: FlagEvent;
  from: string | null;
  to: string | null;
  /** When this page saw it (client clock). */
  at: number;
}

/** The event in a flag change, or null (no change, or nothing to tell: the first flag this page sees is not a change). */
export function flagEvent(
  prev: string | null | undefined,
  next: string | null
): FlagEvent | null {
  if (prev === undefined || prev === next) return null;
  if (next === "Red") return "red";
  if (next === "Checkered") return "checkered";
  if (isCaution(next) && !isCaution(prev)) return "caution";
  if (next === "Green" && (isCaution(prev) || prev === "Red")) return "green";
  return null;
}
